// Fiche technique par indicateur (GO Marc 13/07) — la carte d'identité du chiffre :
// source exacte, producteur, millésime, champ, méthode, limites, fraîcheur TerObs.
// Remplace le badge « Certifié » omniprésent : la preuve à la demande.
import { useEffect, useState } from 'react'
import axios from 'axios'
import { Info, X } from 'lucide-react'
import { API } from './types'

export interface Fiche {
  // `source` et `producteur` manquent sur une fiche du socle quand le catalogue ne les porte
  // pas : on n'invente ni un nom de jeu de données, ni un producteur.
  cle?: string; label: string; source?: string; producteur?: string
  champ?: string; methode?: string; limites?: string; url?: string; licence?: string
  cadence?: string; millesime?: string; verifie_le?: string
  // Champs des anciens instantanés : conservés à la lecture, jamais affichés (24/10/2026).
  provenance?: string
  publie_le?: string; charge_le?: string
  // Fiches du socle (`socle:<id>`, 26/09) : la définition du catalogue et le texte
  // « pourquoi » RELU (le serveur ne sert que `pourquoi_valide`). Les fiches historiques
  // n'en ont pas besoin : leur définition et leur « pourquoi » vivent dans le front
  // (`defs.ts`, `pourquoi.ts`).
  definition?: string; pourquoi?: string
}

const cache = new Map<string, Fiche>()

/** LA façon de charger une fiche, pour la modale comme pour le panneau « Comprendre ce
    chiffre » (lot P, 15/09). Un seul cache : ouvrir le panneau d'une tuile puis la modale
    du bandeau ne redemande pas deux fois la même fiche au serveur.
    `cle` optionnelle — un objet sans fiche appelle le hook comme les autres (un hook ne se
    déclare pas conditionnellement) et n'émet aucune requête. */
export function useFiche(cle?: string, indicateur?: string): { fiche: Fiche | null; erreur: boolean } {
  // LA CLÉ DE CACHE PORTE L'INDICATEUR. Une même fiche sert plusieurs tuiles et peut en
  // spécialiser la méthode et les limites (`par_indicateur`, fiches.py) : mettre les deux
  // réponses sous la même clé afficherait au clic suivant le texte de la tuile précédente.
  // LE CACHE EST LA SEULE SOURCE DE VÉRITÉ, l'état local n'est qu'un déclencheur de
  // rendu. Sans cela il fallait recopier le cache dans l'état à chaque changement de
  // clé — donc écrire dans un effet à chaque passage, donc un rendu de plus pour rien
  // (et un état qui peut diverger du cache le temps d'un rendu). Ici, rien n'est écrit
  // tant qu'une réponse n'est pas arrivée.
  const [, rafraichir] = useState(0)
  const [echecs] = useState(() => new Set<string>())
  const ref = cle ? (indicateur ? `${cle}::${indicateur}` : cle) : undefined
  useEffect(() => {
    if (!cle || !ref || cache.has(ref) || echecs.has(ref)) return
    let alive = true
    const url = `${API}/observatoire/fiche/${cle}`
      + (indicateur ? `?indicateur=${encodeURIComponent(indicateur)}` : '')
    axios.get<Fiche>(url)
      .then(r => { cache.set(ref, r.data); if (alive) rafraichir(n => n + 1) })
      // L'échec est retenu POUR CE MONTAGE : une fiche momentanément indisponible ne
      // rejoue pas sa requête à chaque rendu, et un panneau rouvert plus tard réessaie.
      .catch(() => { echecs.add(ref); if (alive) rafraichir(n => n + 1) })
    return () => { alive = false }
  }, [cle, ref, indicateur, echecs])
  return { fiche: (ref && cache.get(ref)) || null, erreur: !!ref && echecs.has(ref) }
}

export function FicheBtn({ cle }: { cle: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="obs2-fiche-btn"
        aria-label="Fiche technique — d'où vient ce chiffre ?"
        title="D'où vient ce chiffre ?"
        onClick={e => { e.stopPropagation(); setOpen(true) }}>
        <Info size={13} aria-hidden="true" />
      </button>
      {open && <FicheModal cle={cle} onClose={() => setOpen(false)} />}
    </>
  )
}

function Ligne({ t, v }: { t: string; v?: string }) {
  if (!v) return null // un champ absent n'affiche jamais de ligne vide
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: 'var(--c-muted)', fontWeight: 600, marginBottom: 2 }}>{t}</div>
      <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>{v}</div>
    </div>
  )
}

export function FicheModal({ cle, onClose }: { cle: string; onClose: () => void }) {
  const { fiche, erreur } = useFiche(cle)

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  return (
    <div role="dialog" aria-modal="true" aria-label="Fiche technique de l'indicateur"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(13,31,60,.45)', zIndex: 90,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, maxWidth: 540, width: '100%',
                 maxHeight: '85vh', overflowY: 'auto', padding: '22px 24px',
                 boxShadow: '0 18px 50px rgba(13,31,60,.28)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                      gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6,
                          color: 'var(--c-brand)', fontWeight: 700 }}>Fiche technique</div>
            <h4 style={{ margin: '2px 0 0', fontSize: 16.5 }}>{fiche?.label || '…'}</h4>
          </div>
          <button type="button" className="obs2-iconbtn" aria-label="Fermer" onClick={onClose}
            style={{ cursor: 'pointer', flexShrink: 0 }}>
            <X size={15} aria-hidden="true" />
          </button>
        </div>

        {erreur ? (
          <p className="obs2-card-note">Fiche momentanément indisponible — réessayez.</p>
        ) : !fiche ? (
          <p className="obs2-card-note">Chargement…</p>
        ) : (
          <>
            <Ligne t="Source" v={[fiche.source, fiche.producteur].filter(Boolean).join(' — ')} />
            <Ligne t="Millésime des données" v={fiche.millesime} />
            <Ligne t="Champ couvert" v={fiche.champ} />
            <Ligne t="Méthode" v={fiche.methode} />
            <Ligne t="Limites connues" v={fiche.limites} />
            <Ligne t="Mise à jour de la source" v={fiche.cadence} />
            <Ligne t="Licence" v={fiche.licence} />
          </>
        )}
      </div>
    </div>
  )
}
